// React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// MUI
import { Stack } from "@mui/material";
// Third Party
import { get } from "lodash";
import { set } from "lodash-es";

// API
import { getProviderList, updateAdministrationTools } from "@Settings/api/AdministrationTools";

// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// Helpers
import { and, or } from "@Common/helpers/sonar";
import {
	checkAreaNumberError,
	checkStoreNumberError,
	mapNumbers,
} from "@Settings/helpers/AdministrationTools/DealerAdministrationTools/DealerManagementSystemIds";
import { getDisabledAreaNumbersByStoreNumber, getSystemTenant } from "@Settings/api/AdministrationTools/queries";

// Components
import InfoSubTitle from "@Settings/components/SystemSettings/common/InfoSubTitle";
import SmartSelector from "@Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@Layouts/components/InputComponents/SimpleTextField";

// Types
import {
	IAdministrationTools,
	IManagementSystem,
	TUpdateAdministrationToolsManagementSystemResponse,
} from "@Settings/types/administrationTools/model";
import { EManagementSystem } from "@/Settings/types/administrationTools/utils";
import { MANAGEMENT_SYSTEM_SUBSCRIPTION } from "@/Settings/api/AdministrationTools/subscriptions";
import { useEffect } from "react";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { updateAdministrationToolsManagementSystem } from "@/Settings/api/AdministrationTools/mutations";

type CustomerRelationsManagementProps = {
	isExpanded: boolean;
	administrationTools: IAdministrationTools;
};

/********************************************* HELPERS ************************************************/
const STORE_NUMBER_PAD_LENGTH = 3;
const storeNumberOptions = Array.from({ length: 999 }, (_, i) => ({
	label: String(i + 1).padStart(STORE_NUMBER_PAD_LENGTH, "0"),
}));

const AREA_NUMBER_PAD_LENGTH = 2;
const areaNumberOptions = Array.from({ length: 99 }, (_, i) => ({
	label: String(i + 1).padStart(AREA_NUMBER_PAD_LENGTH, "0"),
}));

type LimitedProvided = {
	provider: string;
	providerName: string;
};
export default function CustomerRelationsManagement(props: Readonly<CustomerRelationsManagementProps>) {
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();

	const locationId = useLocationId();

	const integrations = props.administrationTools?.integrations ?? {};
	const { customerRelationshipManagement } = integrations;

	/********************************************* QUERIES ************************************************/
	const { data: providers, isLoading: isLoadingCRMProviders } = useQuery({
		queryKey: ["getCustomerRelationshipManagement"],
		queryFn: () => getProviderList(locationId, EManagementSystem.CRM),
		enabled: and(props.isExpanded, Boolean(locationId)),
	});

	const {
		data: disabledAreaNumbersOptions,
		isLoading: isDisabledAreaNumbersOptionsLoading,
		refetch: refetchDisabledAreaNumbersOptions,
	} = useQuery({
		queryKey: ["getDisabledAreaNumbersByStoreNumber", locationId, EManagementSystem.CRM, customerRelationshipManagement?.storeNumber],
		queryFn: async () =>
			getDisabledAreaNumbersByStoreNumber(locationId, customerRelationshipManagement?.storeNumber, EManagementSystem.CRM),
		enabled: and(Boolean(locationId), Boolean(customerRelationshipManagement?.storeNumber), props.isExpanded),
		staleTime: Infinity,
	});

	/********************************************* MUTATIONS ************************************************/
	const { mutate: mutateCrmProvider, isPending: isMutatingCrmProvider } = useMutation({
		mutationKey: ["changeCRMProvider"],
		mutationFn: async (administrationToolsInput: Partial<IAdministrationTools>) => {
			const provider = get(administrationToolsInput, "integrations.customerRelationshipManagement.provider", "");
			let systemTenant;
			if (provider) {
				systemTenant = await getSystemTenant({ locationId, provider, systemType: EManagementSystem.CRM });
			}

			const updatedAdministrationTools = set(
				administrationToolsInput,
				"integrations.customerRelationshipManagement.systemTenantId",
				systemTenant?.systemTenantId ?? ""
			);

			return updateAdministrationTools(locationId, updatedAdministrationTools);
		},
	});

	const { mutate: mutateStoreId, isPending: isMutatingStoreId } = useMutation({
		mutationKey: ["updateCRMStoreId", locationId],
		mutationFn: (input: IManagementSystem) => updateAdministrationToolsManagementSystem(locationId, EManagementSystem.CRM, input),
		onMutate: (input: IManagementSystem) => {
			const previousValue = integrations?.customerRelationshipManagement?.storeNumber;
			const newValue = input.storeNumber;

			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return set(cachedData, "integrations.customerRelationshipManagement.storeNumber", newValue);
			});
			return { previousValue, newValue };
		},
		onError: (_err, _newValue, context) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return set(cachedData, "integrations.customerRelationshipManagement.storeNumber", context?.previousValue);
			});
		},
	});

	const { mutate: mutateBranchId, isPending: isMutatingBranchId } = useMutation({
		mutationKey: ["updateCRMBranchId", locationId],
		mutationFn: (input: IManagementSystem) => updateAdministrationToolsManagementSystem(locationId, EManagementSystem.CRM, input),
		onMutate: (input: IManagementSystem) => {
			const previousValue = integrations?.customerRelationshipManagement?.areaNumber;
			const areaNumber = input.areaNumber;

			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return set(cachedData, "integrations.customerRelationshipManagement.areaNumber", areaNumber);
			});
			return { previousValue, newValue: areaNumber };
		},
		onError: (_err, _newValue, context) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return set(cachedData, "integrations.customerRelationshipManagement.areaNumber", context?.previousValue);
			});
		},
	});

	/********************************************* SUBSCRIPTIONS ********************************************/
	const managementSystemSubscription = getSubscription([EManagementSystem.CRM, customerRelationshipManagement?.storeNumber]);
	managementSystemSubscription?.subscriptions?.onUpdateAdministrationToolsManagementSystem?.setCallback(
		(
			response: GraphqlSubscriptionResponse<
				"onUpdateAdministrationToolsManagementSystem",
				TUpdateAdministrationToolsManagementSystemResponse
			>
		) => {
			const updatedCrmProvider = response.data?.onUpdateAdministrationToolsManagementSystem;
			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools | undefined) => {
				if (!cachedData) {
					return cachedData;
				}

				const { locationId: updatedLocationId, managementSystem } =
					updatedCrmProvider as TUpdateAdministrationToolsManagementSystemResponse;

				if (updatedLocationId !== locationId) {
					return cachedData;
				}

				refetchDisabledAreaNumbersOptions();

				return {
					...cachedData,
					integrations: {
						...cachedData.integrations,
						customerRelationshipManagement: managementSystem,
					},
				};
			});
		}
	);
	/********************************************* HANDLERS ************************************************/
	const handleChangeCRMProvider = (newProvider: LimitedProvided) => {
		const updatedIntegrations = {
			...integrations,
			customerRelationshipManagement: {
				...integrations.customerRelationshipManagement,
				provider: newProvider.provider,
				providerName: newProvider.providerName,
			},
		};
		mutateCrmProvider({ integrations: updatedIntegrations });
	};

	const handleChangeStoreId = (newStoreId: string) => {
		// If it haven't changed the Store ID, don't do anything
		if (newStoreId === integrations?.customerRelationshipManagement?.storeNumber) {
			return;
		}

		const updatedCRM = {
			...integrations.customerRelationshipManagement,
			storeNumber: newStoreId,
			areaNumber: [], // Reset area numbers when store number changes
		};

		mutateStoreId(updatedCRM);
	};

	const handleChangeBranchId = (branchIds: { label: string }[]) => {
		// If it haven't changed the area numbers selected, don't do anything
		if (branchIds?.length === integrations?.customerRelationshipManagement?.areaNumber?.length) {
			return;
		}
		const normalizedBranchIds = branchIds.map((branchId) => branchId.label);
		const updatedCRM = {
			...integrations.customerRelationshipManagement,
			areaNumber: normalizedBranchIds,
		};

		mutateBranchId(updatedCRM);
	};

	useEffect(() => {
		addSubscription(
			[EManagementSystem.CRM, customerRelationshipManagement?.storeNumber],
			[
				{
					context: "onUpdateAdministrationToolsManagementSystem",
					query: MANAGEMENT_SYSTEM_SUBSCRIPTION,
					variables: {
						managementSystemType: EManagementSystem.CRM,
						storeNumber: customerRelationshipManagement?.storeNumber,
					},
				},
			]
		);
		return () => {
			deleteSubscription([EManagementSystem.CRM, customerRelationshipManagement?.storeNumber]);
		};
	}, [customerRelationshipManagement?.storeNumber]);

	return (
		<Stack component={"section"} spacing={3}>
			{/*	CRM Provider*/}
			<Stack spacing={1.5}>
				<InfoSubTitle title={"CRM Provider"} />
				<SmartSelector
					// Data Props
					options={(providers ?? []) as object[]}
					selected={customerRelationshipManagement?.providerName && { providerName: customerRelationshipManagement.providerName }}
					loading={or(isLoadingCRMProviders, isMutatingCrmProvider)}
					disabled={or(isLoadingCRMProviders, isMutatingCrmProvider)}
					// Option Props
					clearable
					selectBy={"providerName"}
					showBy={"providerName"}
					// Style Props
					sx={{ width: 210 }}
					inputProps={{ placeholder: "CRM Provider", sx: { width: 210 } }}
					popperProps={{ className: "!z-[1350]" } as object}
					// Callbacks
					onChangeAction={(value) => handleChangeCRMProvider(value as LimitedProvided)}
				/>
			</Stack>

			{/*	 System Tenant Information Related */}
			<Stack direction={"row"} spacing={3}>
				<Stack spacing={1.5}>
					<InfoSubTitle title={"System ID"} />
					<SimpleTextField
						// Data Props
						text={customerRelationshipManagement?.systemTenantId}
						// Option Props
						disabled
						outlined
						editable={false}
						// Style Props
						placeholder={"System ID"}
						classes={"!w-[210px]"}
					/>
				</Stack>
				<Stack spacing={1.5}>
					<InfoSubTitle title={"Store ID"} />
					<SmartSelector
						// Data Props
						options={storeNumberOptions}
						selected={customerRelationshipManagement?.storeNumber && { label: customerRelationshipManagement?.storeNumber }}
						disabled={isMutatingStoreId || !customerRelationshipManagement?.providerName}
						error={checkStoreNumberError(customerRelationshipManagement?.storeNumber, storeNumberOptions)}
						// Options Props
						clearable
						showBy={"label"}
						selectBy={"label"}
						// Style Props
						inputProps={{
							placeholder: "Store ID",
							sx: { width: 210 },
						}}
						popperProps={{ className: "!z-[1350]" } as object}
						// Callbacks
						onChangeAction={(value) => handleChangeStoreId((value as { label: string })?.label)}
					/>
				</Stack>
				<Stack spacing={1.5}>
					<InfoSubTitle title={"Branch ID"} />
					<SmartSelector
						// Data Props
						options={areaNumberOptions}
						disabledOptions={disabledAreaNumbersOptions ?? []}
						selected={customerRelationshipManagement?.areaNumber && mapNumbers(customerRelationshipManagement?.areaNumber)}
						disabled={or(
							isMutatingBranchId,
							!customerRelationshipManagement?.providerName,
							!customerRelationshipManagement?.storeNumber
						)}
						loading={isDisabledAreaNumbersOptionsLoading}
						error={checkAreaNumberError(customerRelationshipManagement?.areaNumber, areaNumberOptions)}
						// Option Props
						multiple
						showBy={"label"}
						selectBy={"label"}
						// Style Props
						inputProps={{
							placeholder: "Branch ID",
							sx: { width: 210 },
						}}
						renderTagsCustomProps={{
							classes: "!font-sans",
						}}
						popperProps={{ className: "!z-[1350]" } as object}
						// Callbacks
						onChangeAction={(value) => handleChangeBranchId(value as { label: string }[])}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
}
